import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {SchedulerCalendar} from "./SchedulerCalendar";
import {FormErrorDialog} from "../../../../error/FormErrorDialog";
import {setPlansUpdated, setUpdatingPlansError, updatePlans} from "../../../../../slices/menuSlice";
import {FormHeader} from "../../shared-components/FormHeader";
import {useTranslation} from "react-i18next";
import {fetchActiveMenu, setSchedulerActive} from "../../../../../slices/cmsSlice";
import {useFetchCurrentRestaurant} from "../../../../../hooks/useFetchCurrentRestaurant";
import {useConfirmationMessage} from "../../../../../hooks/useConfirmationMessage";
import {BorderedButton} from "../../../common/BorderedButton";
import {TrashIcon} from "../../../../icons/TrashIcon";
import {StarIcon} from "../../../../icons/StarIcon";
import {fillGapsWithStandard} from "../../../../../utils/schedulerUtils";
import {useWindowWidth} from "../../../../../hooks/useWindowWidth";

export const Scheduler = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {restaurant} = useSelector((state) => state.dashboard.view);
    const {updatingPlansError} = useSelector((state) => state.menu.updatePlans);
    const trashRef = useRef(null);
    const getRestaurant = useFetchCurrentRestaurant();
    const renderConfirmation = useConfirmationMessage(setPlansUpdated);
    const {isLoading} = useSelector((state) => state.menu.updatePlans);
    const restaurantSettings = restaurant?.value?.settings;
    const operatingHours = restaurantSettings.operatingHours;
    const windowWidth = useWindowWidth();
    const isMobile = windowWidth <= 1000;

    const initialMenus = useMemo(() => {
        return [...(restaurant?.value.menus || [])].sort((a, b) => {
            if (a.standard !== b.standard) {
                return a.standard ? -1 : 1;
            }
            return a.name.localeCompare(b.name, 'pl', {sensitivity: 'base'});
        });
    }, [restaurant.value.menus]);

    const [menusConfig, setMenusConfig] = useState(initialMenus);

    useEffect(() => {
        setMenusConfig(initialMenus);
    }, [initialMenus]);

    const handleDiscard = () => {
        dispatch(setSchedulerActive(false));
        dispatch(setUpdatingPlansError(null));
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        const resultAction = await dispatch(updatePlans({menus: menusConfig}));
        if (updatePlans.fulfilled.match(resultAction)) {
            await getRestaurant();
            await dispatch(fetchActiveMenu());
            dispatch(setSchedulerActive(false));
            renderConfirmation();
        } else {
            dispatch(setUpdatingPlansError(resultAction?.payload));
        }
    };

    const clearSchedules = () => {
        setMenusConfig(prevMenus =>
            prevMenus.map(menu => ({
                ...menu,
                plan: []
            }))
        );
    };

    const fillWithStandard = useCallback(() => {
        fillGapsWithStandard(setMenusConfig, operatingHours);
    }, [operatingHours, setMenusConfig]);

    const customButtons = [
        <BorderedButton onClick={clearSchedules}
                        text={t('clearSchedule')}
                        icon={<TrashIcon removeWrapper={true} fill={'#222'}/>}
                        isBordered={true}
                        isMobile={isMobile}/>,
        <BorderedButton onClick={fillWithStandard}
                        text={t('fillEmptyWithMain')}
                        icon={<StarIcon removeWrapper={true} stroke={'#8540DD'}/>}
                        isBordered={true}
                        isMobile={isMobile}/>
    ];

    const renderCustomButtons = () => {
        return customButtons.map((button, i) => (<div key={i}>{button}</div>));
    }

    return (
        <div className={'background'}>
            <FormErrorDialog errorData={updatingPlansError} setErrorData={setUpdatingPlansError}/>
            <div className={'scheduler-wrapper'}>
                <FormHeader formHeader={t('menuSchedules')}
                            onFormSubmit={handleSubmit}
                            onFormDiscard={handleDiscard}
                            isLoading={isLoading}
                            renderCustomButtons={renderCustomButtons}
                />
                <SchedulerCalendar menusConfig={menusConfig}
                                   setMenusConfig={setMenusConfig}
                                   trashRef={trashRef}/>
            </div>
        </div>
    );
};
